/**
 * audioEchoCancellation.js
 *
 * Client-Side Reference Acoustic Echo Cancellation (AEC) & Barge-In Detection
 * Mirrors MS Teams / WebRTC echo suppression by routing the AI's playback audio
 * through the Web Audio graph alongside the microphone stream.
 */

let globalAudioContext = null;
const connectedElements = new WeakSet();

/**
 * Get or create the shared AudioContext for reference AEC processing.
 */
export function getSharedAudioContext() {
  if (!globalAudioContext || globalAudioContext.state === 'closed') {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      globalAudioContext = new AudioCtx();
    }
  }
  if (globalAudioContext && globalAudioContext.state === 'suspended') {
    globalAudioContext.resume().catch((e) => console.warn('AudioContext resume:', e));
  }
  return globalAudioContext;
}

/**
 * Cleanly close the shared AudioContext when an interview ends.
 */
export function closeSharedAudioContext() {
  if (globalAudioContext && globalAudioContext.state !== 'closed') {
    try {
      globalAudioContext.close().catch(() => {});
    } catch (_) {}
  }
  globalAudioContext = null;
}

/**
 * Bridges David's HTMLAudioElement into the Web Audio graph so the browser's
 * hardware and DSP AEC engine receives the exact outbound reference signal.
 * Safe no-op if already managed by VideoRecorderWithMixedAudio.
 */
export function attachAudioElementAsReference(audioElement) {
  // VideoRecorderWithMixedAudio already binds questionAudioElement to its AudioContext,
  // connects it to destination, and feeds questionAnalyser & micAnalyser.
  // We avoid creating a duplicate AudioContext to prevent InvalidStateError.
  if (!audioElement) return;
  console.log('🎙️ Audio playback element ready for AEC & analyser routing');
}

/**
 * Comprehensive interruption patterns for real-time Barge-In during AI speech.
 * Handles natural conversational variations, name prefixes ("David", "Hey David"),
 * multi-word requests, and common acoustic speech-to-text homophones ("weights", "weight", "paws", "stopped").
 */
const INTERRUPTION_PATTERNS = [
  // Direct Stop/Wait stems, homophones, and halt commands (with optional conversational prefixes like "David", "Hey", "Please", "Okay")
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:wait|waits|waiting|waited|weight|weights|way|wate|wait\s+up)\b/i,
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:stop|stops|stopping|stopped|stopp|freeze|halt|chill|whoa|woah)\b/i,
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:pause|pauses|pausing|paused|paws)\b/i,
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:hold\s*up|hold\s*on|holdup|hang\s*on|hold\s*off|hold\s*it|hold\s+your\s+horses)\b/i,
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:listen|listens|listening|listen\s+to\s+me|hear\s+me\s+out)\b/i,
  /\b(?:shut\s*up|be\s*quiet|quiet|enough|shh|shush)\b/i,

  // Candidate readiness, confidence & direct answer initiation
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:i\s+got\s+this|i\s+got\s+it|i've\s+got\s+this|i\s+have\s+got\s+this|i'm\s+ready|i\s+am\s+ready|let\s+me\s+answer|i\s+can\s+answer)\b/i,
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:i\s+know\s+the\s+answer|i\s+know\s+this|i\s+know\s+it|i\s+already\s+know|no\s+need\s+to\s+continue|no\s+need\s+to\s+explain|that's\s+enough)\b/i,
  /\b(?:hey\s+|david\s+|ok\s+|okay\s+|please\s+)*(?:stop\s+dictating|stop\s+reading|stop\s+talking)\b/i,

  // Repetition & question dictation controls
  /\b(?:can|could|would)\s+(?:you\s+)?(?:please\s+)?(?:repeat|say\s+again|go\s+over)\b/i,
  /\b(?:repeat|re-describe|redescribe)(\s+the|\s+this)?\s+question\b/i,
  /\b(?:say\s+that\s+again|say\s+again|one\s+more\s+time|pardon|pardon\s+me|come\s+again|once\s+again)\b/i,
  /\b(?:didn't\s+catch\s+that|didn't\s+hear\s+that|what\s+did\s+you\s+say)\b/i,

  // Polite thinking pauses & time requests
  /\b(?:give\s+me\s+a\s+(?:second|sec|minute|moment|tick)|one\s+(?:second|sec|minute|moment)|just\s+a\s+(?:second|sec|minute|moment|tick))\b/i,
  /\b(?:excuse\s+me|hang\s+on\s+a\s+(?:sec|second|minute)|wait\s+a\s+(?:sec|second|minute|moment)|let\s+me\s+(?:think|check))\b/i,
];

/**
 * Check if an interim speech transcript represents a candidate barge-in/interruption.
 */
export function isInterruptionPhrase(text) {
  if (!text || typeof text !== 'string') return false;
  // Normalize punctuation (replace hyphens, commas, periods, etc. with spaces)
  const normalized = text
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return false;
  return INTERRUPTION_PATTERNS.some((pattern) => pattern.test(normalized));
}

/**
 * Acquire microphone media stream with optimal Acoustic Echo Cancellation and noise reduction constraints.
 */
export async function getAecMicrophoneStream() {
  const constraints = {
    audio: {
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      autoGainControl: { ideal: true },
      googEchoCancellation: { ideal: true },
      googAutoGainControl: { ideal: true },
      googNoiseSuppression: { ideal: true },
      googHighpassFilter: { ideal: true },
    },
    video: false,
  };

  return navigator.mediaDevices.getUserMedia(constraints);
}
